import type React from 'react';
import { useEffect, useState } from 'react';
import cx from 'classnames';
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Box, Button, ButtonGroup, FormField, FormHelp, Input, Label, Text } from '@zillow/constellation-10';

import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import mastercardPng from '@assets/mastercard.png';
import visaPng from '@assets/visa.png';
import discoverPng from '@assets/discover.png';
import amexPng from '@assets/amex.png';
import { isEmpty } from '@common/paymentsUtils/inputValidator';
import errorMessages from '@common/constants/errorMessages';
import * as styles from './index.scss';
import { logError } from '@zg-rentals/log-error';

interface Props {
  modeOfPayment?: string;
  onContinue: (...args: any) => any;
  onBack: () => any;
  // for storybook
  nextStepForStorybook?: () => void;
}

const CardSection = (props: Props) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  });

  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState({ value: '', error: '' });
  const [cardNumber, setCardNumber] = useState({ focused: false, error: '' });
  const [expDate, setExpDate] = useState({ focused: false, error: '' });
  const [cvc, setCvc] = useState({ focused: false, error: '' });
  const [accountNickname, setAccountNickname] = useState('');
  const [loading, setLoading] = useState(false);
  const labelToSetState = (label: string): React.Dispatch<React.SetStateAction<any>> => {
    switch (label) {
      case 'name':
        return setName;
      case 'cardNumber':
        return setCardNumber;
      case 'expDate':
        return setExpDate;
      case 'cvc':
        return setCvc;
      default:
        throw new Error('invalid label name');
    }
  };

  function updateState(label: string, newState: Record<string, any>) {
    labelToSetState(label)((previousState: Record<string, any>) => {
      return {
        ...previousState,
        ...newState,
      };
    });
  }

  const handleOnFocus = (label: string) => updateState(label, { focused: true });
  const handleOnBlur = (label: string) => updateState(label, { focused: false });
  const handleOnChange = (event, label: string) => {
    const errorMessage = ((event || {}).error || {}).message || '';
    updateState(label, { error: errorMessage });
  };

  const onSubmit = (e) => {
    // If nextStepForStorybook is provided (for stories), use it instead
    if (props.nextStepForStorybook) {
      props.nextStepForStorybook();
      return;
    }

    e.preventDefault();
    setLoading(true);
    if (isEmpty(name.value)) {
      setLoading(false);
      updateState('name', { error: errorMessages.PAYMENT_CARD_NAME_EMPTY });
      return;
    }
    const cardElement = elements!.getElement(CardNumberElement);
    const customerInfo = { name: name.value };
    createStripeToken(cardElement, customerInfo);
  };

  const getInputStyleForBrand = () => {
    const brand = 'zillow';
    return {
      style: {
        base: {
          color: styles[`${brand}-color`],
          fontSize: styles[`${brand}-font-size`],
          fontFamily: styles[`${brand}-font-family`],
          fontSmoothing: 'antialiased',
          '::placeholder': {
            color: styles[`${brand}-placeholder-color`],
          },
        },
      },
    };
  };

  const getClassName = (state) =>
    cx('Input Stripe-element', 'fs-exclude', {
      'Stripe-element-focus': state.focused && !state.error,
      'Stripe-element-error': state.error,
    });

  const createStripeToken = async (element, customerInfo) => {
    const result = await stripe!.createToken(element, customerInfo);
    setLoading(false);
    if (result.error) {
      updateState('cardNumber', { error: result.error.message || '' });
      logError({ error: result.error, context: 'Error creating token' });
      return;
    }

    if (!result.token.card) {
      updateState('cardNumber', { error: errorMessages.PAYMENT_TYPE_NOT_VALID });
    }
    if (result.token.card?.funding !== props.modeOfPayment) {
      updateState('cardNumber', { error: errorMessages.PAYMENT_TYPE_MISMATCH(props.modeOfPayment) });
      return;
    }

    if (
      result.token.card?.brand !== 'Visa' &&
      result.token.card?.brand !== 'MasterCard' &&
      result.token.card?.brand !== 'Discover' &&
      result.token.card?.brand !== 'American Express'
    ) {
      updateState('cardNumber', { error: errorMessages.PAYMENT_INVALID_TYPE_CARD });
      return;
    }
    props.onContinue({ stripeToken: result.token.id, accountNickname });
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'token(spacing.sm)' }}>
      <Box css={{ display: 'flex', flexDirection: 'column' }}>
        <Label className="man">Card number</Label>
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: '9fr 3fr',
            alignItems: 'center',
          }}
        >
          <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
            We currently accept
          </Text>
          <Box
            css={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
            }}
          >
            <img alt="card-image" src={discoverPng as unknown as string} style={{ height: '15px' }} />
            <img alt="card-image" src={visaPng as unknown as string} style={{ height: '15px' }} />
            <img alt="card-image" src={mastercardPng as unknown as string} style={{ height: '15px' }} />
            <img alt="card-image" src={amexPng as unknown as string} style={{ height: '15px' }} />
          </Box>
        </Box>
        <Box css={{ border: '1px solid #d9d9d9' }} className={getClassName(cardNumber)}>
          <CardNumberElement
            options={getInputStyleForBrand()}
            onFocus={() => handleOnFocus('cardNumber')}
            onBlur={() => handleOnBlur('cardNumber')}
            onChange={(e) => handleOnChange(e, 'cardNumber')}
          />
        </Box>
        {cardNumber.error && (
          <Text textStyle="body-sm" css={{ color: 'red.500' }}>
            {cardNumber.error}
          </Text>
        )}
        <Box></Box>
      </Box>
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px',
        }}
      >
        <Box>
          <Label>Expiration date</Label>
          <Box css={{ border: '1px solid #d9d9d9' }} className={getClassName(cardNumber)}>
            <CardExpiryElement
              options={getInputStyleForBrand()}
              onFocus={() => handleOnFocus('expDate')}
              onBlur={() => handleOnBlur('expDate')}
              onChange={(e) => handleOnChange(e, 'expDate')}
            />
          </Box>
          {expDate.error && (
            <Text textStyle="body-sm" css={{ color: 'red.500' }}>
              {expDate.error}
            </Text>
          )}
        </Box>
        <Box>
          <Label>CVC</Label>
          <Box css={{ border: '1px solid #d9d9d9' }} className={getClassName(cardNumber)}>
            <CardCvcElement
              options={getInputStyleForBrand()}
              onFocus={() => handleOnFocus('cvc')}
              onBlur={() => handleOnBlur('cvc')}
              onChange={(e) => handleOnChange(e, 'cvc')}
            />
          </Box>
        </Box>
      </Box>
      <FormField
        control={
          <Input
            data-id="name"
            placeholder="Full name"
            onChange={(e) => setName({ value: e.currentTarget.value, error: '' })}
            error={Boolean(name.error)}
          />
        }
        label={<Label>Name on card</Label>}
        description={name.error && <FormHelp error>{name.error}</FormHelp>}
      />
      <FormField
        control={<Input onChange={(e) => setAccountNickname(e.currentTarget.value)} />}
        label={<Label optional>Account nickname</Label>}
        description={name.error && <FormHelp error>{name.error}</FormHelp>}
      />
      <ButtonGroup aria-label="Modal actions" role="group">
        <Button size="lg" fluid={true} onClick={props.onBack}>
          Back
        </Button>
        <DebouncedButton size="lg" fluid={true} onClick={onSubmit} loading={loading}>
          Continue
        </DebouncedButton>
      </ButtonGroup>
    </Box>
  );
};

export default CardSection;
